/**
 * Phase B.4 — Listener middleware for Lab Assistant billing.
 *
 * Listens to every fulfilled / rejected RTK action whose payload
 * matches the Lab Assistant mutations and:
 *
 *  - On success with a billing snapshot → dispatches
 *    `updateFromResponse` so the balance widget stays live without
 *    an extra round-trip to /me/entitlements.
 *  - On rejection with 402 INSUFFICIENT_CREDITS or 403
 *    NO_LAB_ASSISTANT_CREDITS → dispatches
 *    `openInsufficientCreditsModal` so the global modal shows
 *    automatically — every action button (Compose, Optimize,
 *    Refine, Translate, Coach, Explain) gets the same UX without
 *    each surface having to handle 402 individually.
 *
 * Middleware is the right layer for this (rather than per-mutation
 * onQueryStarted callbacks) because (a) it scales to all LA
 * mutations including future ones, (b) keeps surfaces ignorant of
 * billing concerns.
 */
import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { registryApi } from '../services/registry';
import {
  updateFromResponse,
  openInsufficientCreditsModal,
  InsufficientCreditsErrorCode,
} from './lab-assistant-billing.slice';

export const labAssistantBillingMiddleware = createListenerMiddleware();

// Mutations that go through Lab Assistant and may carry billing
// snapshots / 402 errors. Auto-name + auto-tag are intentionally
// excluded — they're internal callers (no user, no billing) per
// the B.2 contract.
const labAssistantMutationMatchers = [
  registryApi.endpoints.runMetaprompt.matchFulfilled,
  registryApi.endpoints.runCoach.matchFulfilled,
  registryApi.endpoints.runOptimize.matchFulfilled,
  registryApi.endpoints.runOptimizeForCohort.matchFulfilled,
  registryApi.endpoints.runRefine.matchFulfilled,
  registryApi.endpoints.runTranslateSyntax.matchFulfilled,
  registryApi.endpoints.runExplain.matchFulfilled,
] as const;

const labAssistantRejectionMatchers = [
  registryApi.endpoints.runMetaprompt.matchRejected,
  registryApi.endpoints.runCoach.matchRejected,
  registryApi.endpoints.runOptimize.matchRejected,
  registryApi.endpoints.runOptimizeForCohort.matchRejected,
  registryApi.endpoints.runRefine.matchRejected,
  registryApi.endpoints.runTranslateSyntax.matchRejected,
  registryApi.endpoints.runExplain.matchRejected,
] as const;

// ─── Success path ─────────────────────────────────────────────────
labAssistantBillingMiddleware.startListening({
  matcher: isAnyOf(...labAssistantMutationMatchers),
  effect: async (action, listenerApi) => {
    const payload = (action as { payload?: unknown }).payload as
      | {
          creditsRemaining?: { plan: number; purchased: number };
          creditMultiplier?: number;
        }
      | undefined;
    if (!payload) return;
    if (!payload.creditsRemaining && typeof payload.creditMultiplier !== 'number') {
      return; // Internal caller — no billing on this response
    }
    listenerApi.dispatch(
      updateFromResponse({
        creditsRemaining: payload.creditsRemaining,
        creditMultiplier: payload.creditMultiplier,
      }),
    );
  },
});

// ─── Rejection path ───────────────────────────────────────────────
labAssistantBillingMiddleware.startListening({
  matcher: isAnyOf(...labAssistantRejectionMatchers),
  effect: async (action, listenerApi) => {
    const error = (action as { payload?: unknown; error?: unknown }).payload as
      | { status?: number; data?: any }
      | undefined;
    if (!error) return;

    // RTK packages errors as `{ status: <httpCode>, data: <body> }`.
    const status = error.status;
    const body = error.data;
    if (status !== 402 && status !== 403) return;

    const code = body?.code as InsufficientCreditsErrorCode | undefined;
    if (code !== 'INSUFFICIENT_CREDITS' && code !== 'NO_LAB_ASSISTANT_CREDITS') {
      return;
    }

    listenerApi.dispatch(
      openInsufficientCreditsModal({
        code,
        planName: body?.details?.planName,
        // The action `type` looks like
        // `registryApi/executeMutation/rejected` — not very
        // descriptive. We pull operation from the `meta.arg`
        // (the request body) when present.
        operation: (action as any)?.meta?.arg?.endpointName,
        message: body?.message,
      }),
    );
  },
});
